"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="19385" className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-secondary/20 p-4">
          <Send className="size-12 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-display font-bold text-white">Quote Request Sent</h3>
          <p className="text-white/70 max-w-md mx-auto font-sans">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary font-sans uppercase tracking-widest">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary h-12 transition-all duration-300 shadow-inner" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {/* Email */}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary h-12 transition-all duration-300 shadow-inner" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Phone */}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="555-0123" {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary h-12 transition-all duration-300 shadow-inner" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {/* Company/Service Type */}
        <FormField control={control} name="company" render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
                Service Type
              </FormLabel>
              <FormControl>
                <Input placeholder="Airport Transfer, Wedding, etc." {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary h-12 transition-all duration-300 shadow-inner" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {/* Subject/Pickup */}
      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
              Pickup Location
            </FormLabel>
            <FormControl>
              <Input placeholder="City or Airport Code" {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary h-12 transition-all duration-300 shadow-inner" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {/* Message/Details */}
      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="font-sans text-xs font-semibold text-white/80 uppercase tracking-widest">
              Travel Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Date, time, and any special requests..." {...field} className="bg-white/90 border-gray-200 text-black placeholder:text-gray-400 rounded-md focus-visible:ring-secondary focus-visible:border-secondary min-h-[160px] resize-y transition-all duration-300 shadow-inner" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium text-center">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold tracking-widest uppercase bg-primary hover:bg-primary-hover text-foreground-primary rounded-md relative overflow-hidden group transition-all duration-300 shadow-medium">
        <span className="relative z-10 flex items-center justify-center gap-2">
          {isSubmitting ? <>
              <Loader className="animate-spin size-5" />
              Processing...
            </> : "[ GET QUOTE ]"}
        </span>
        {/* Shimmer Effect */}
        <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent z-0" />
      </Button>
    </Form>;
}
export default function ContactForm() {
  return <section id="get-a-quote" className="relative py-24 px-4 md:px-8 bg-dark-section text-dark-foreground overflow-hidden">
      {/* Subtle Background Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--color-primary-dark)_0%,_transparent_70%)] opacity-30 pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-display font-bold tracking-tight text-white uppercase">
            GET A QUOTE
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto font-sans text-lg">
            Request a bespoke travel solution. For immediate assistance, contact our 24/7 concierge at <span className="text-secondary font-bold">1-800-GOLD-LIMO</span>.
          </p>
        </div>

        {/* Glassmorphism Panel */}
        <div className="backdrop-blur-md bg-black/60 border border-white/10 rounded-lg p-8 md:p-20 shadow-strong ring-1 ring-white/5">
          <FormProvider formKey="quote_form_losangelesluxurytransportation" formId="quote_form_losangelesluxurytransportation" sectionName="contact-section" defaultValues={{
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          subject: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="[ GET QUOTE ]" successMessage="Thank you! Your quote request has been received. Our concierge will contact you shortly." errorMessage="We couldn't process your request. Please call us directly at 1-800-GOLD-LIMO.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}